import { useState } from 'react';
import '@/App.css';
import { Menu, X, ChevronDown, Mail, Phone, Globe, Award, Users, Calendar, TrendingUp, Zap, Shield, BarChart3, MessageSquare, Headphones } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';

function Home() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setMobileMenuOpen(false);
    }
  };

  return (
    <div className="app-container">
      {/* Navigation */}
      <nav className="nav-bar">
        <div className="nav-content">
          <div className="nav-logo" data-testid="nav-logo">
            <img src="https://customer-assets.emergentagent.com/job_pe360-global/artifacts/7bbv220l_pharmaevents%20No%20Background%20%281%29-cropped%20%281%29.svg" alt="PharmaEvents360" />
          </div>
          
          {/* Desktop Navigation */}
          <div className="nav-links-desktop">
            <button onClick={() => scrollToSection('about')} data-testid="nav-about">About</button>
            <button onClick={() => scrollToSection('technology')} data-testid="nav-technology">Technology</button>
            <button onClick={() => scrollToSection('expertise')} data-testid="nav-expertise">Expertise</button>
            <button onClick={() => scrollToSection('global')} data-testid="nav-global">Global Reach</button>
            <button onClick={() => window.location.href = '/gallery'} data-testid="nav-gallery">Gallery</button>
            <button onClick={() => scrollToSection('contact')} data-testid="nav-contact">Contact</button>
          </div>

          {/* Mobile Menu Button */}
          <button className="mobile-menu-btn" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-btn">
            {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="nav-links-mobile" data-testid="mobile-menu">
            <button onClick={() => scrollToSection('about')}>About</button>
            <button onClick={() => scrollToSection('technology')}>Technology</button>
            <button onClick={() => scrollToSection('expertise')}>Expertise</button>
            <button onClick={() => scrollToSection('global')}>Global Reach</button>
            <button onClick={() => window.location.href = '/gallery'}>Gallery</button>
            <button onClick={() => scrollToSection('contact')}>Contact</button>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="hero-section" data-testid="hero-section">
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <h1 className="hero-title" data-testid="hero-title">
            Events are our Passion — Not Just Profession.
          </h1>
          <p className="hero-subtitle" data-testid="hero-subtitle">
            15+ years of delivering global events with pharma-grade precision and creative excellence.
          </p>
          <div className="hero-buttons">
            <button onClick={() => scrollToSection('expertise')} className="hero-btn-primary" data-testid="hero-explore-btn">
              Explore Capabilities
            </button>
          </div>
        </div>
        <div className="scroll-indicator" onClick={() => scrollToSection('about')}>
          <ChevronDown size={32} />
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="section-white" data-testid="about-section">
        <div className="section-container">
          <h2 className="section-title" data-testid="about-title">15+ Years of Event Excellence</h2>
          <div className="section-divider"></div>
          
          <p className="about-intro" data-testid="about-intro">
            Our comprehensive suite of event services caters to a diverse clientele, ranging from corporates to families looking for experiences to create moments to cherish for a lifetime.
          </p>

          <div className="stats-grid">
            <div className="stat-card" data-testid="stat-established">
              <div className="stat-icon"><Calendar /></div>
              <div className="stat-number">2009</div>
              <div className="stat-label">Established</div>
            </div>
            <div className="stat-card" data-testid="stat-events">
              <div className="stat-icon"><Award /></div>
              <div className="stat-number">150+</div>
              <div className="stat-label">Events Delivered<br/>(2009-2025)</div>
            </div>
            <div className="stat-card" data-testid="stat-countries">
              <div className="stat-icon"><Globe /></div>
              <div className="stat-number">30+</div>
              <div className="stat-label">Countries | 3 Continents</div>
            </div>
            <div className="stat-card" data-testid="stat-experience">
              <div className="stat-icon"><Users /></div>
              <div className="stat-number">200+</div>
              <div className="stat-label">Years Cumulative Experience</div>
            </div>
            <div className="stat-card" data-testid="stat-specialization">
              <div className="stat-icon"><TrendingUp /></div>
              <div className="stat-number">60/30/10</div>
              <div className="stat-label">Pharma / Corporate / Social</div>
            </div>
            <div className="stat-card" data-testid="stat-hybrid">
              <div className="stat-icon"><Zap /></div>
              <div className="stat-number">20%</div>
              <div className="stat-label">Hybrid Format Events</div>
            </div>
          </div>

          <div className="milestones" data-testid="milestones">
            <h3 className="milestones-title">Recent Milestones</h3>
            <div className="milestones-list">
              <span className="milestone-item">HPSN 2023 (Bangalore)</span>
              <span className="milestone-divider">•</span>
              <span className="milestone-item">APLAR 2023 (Thailand)</span>
              <span className="milestone-divider">•</span>
              <span className="milestone-item">Takeda ICC Floor Launch</span>
              <span className="milestone-divider">•</span>
              <span className="milestone-item">Takeda Investigator Meeting (Singapore)</span>
            </div>
          </div>
        </div>
      </section>

      {/* Technology Section */}
      <section id="technology" className="section-gray" data-testid="technology-section">
        <div className="section-container">
          <h2 className="section-title" data-testid="tech-title">Technology-Driven Excellence</h2>
          <div className="section-divider"></div>

          <div className="tech-grid">
            <div className="tech-card" data-testid="tech-pharma-expertise">
              <div className="tech-icon"><Shield /></div>
              <h3>Pharma/Clinical Trial Industry Expertise</h3>
            </div>
            <div className="tech-card" data-testid="tech-track-record">
              <div className="tech-icon"><Award /></div>
              <h3>16-Year Proven Track Record</h3>
            </div>
            <div className="tech-card" data-testid="tech-hybrid">
              <div className="tech-icon"><Zap /></div>
              <h3>Hybrid Event Technology</h3>
            </div>
            <div className="tech-card" data-testid="tech-ai">
              <div className="tech-icon"><TrendingUp /></div>
              <h3>AI-Powered Personalization</h3>
            </div>
            <div className="tech-card" data-testid="tech-global">
              <div className="tech-icon"><Globe /></div>
              <h3>100+ Global Events Delivered</h3>
            </div>
            <div className="tech-card" data-testid="tech-support">
              <div className="tech-icon"><Headphones /></div>
              <h3>24/7 White-Glove Support</h3>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="section-white" data-testid="why-choose-section">
        <div className="section-container">
          <h2 className="section-title" data-testid="why-title">Why Leading Enterprises Choose <img src="https://customer-assets.emergentagent.com/job_pe360-global/artifacts/xewkvkdw_PE360%20logo_No%20Background_cropped.svg" alt="PE360" className="inline-logo-heading" /></h2>
          <div className="section-divider"></div>

          <div className="why-content">
            <div className="why-points">
              <div className="why-point" data-testid="why-insider">
                <div className="why-icon">★</div>
                <p><strong>Industry insider knowledge</strong> — Core team with pharma, clinical trial & event backgrounds</p>
              </div>
              <div className="why-point" data-testid="why-compliance">
                <div className="why-icon">★</div>
                <p><strong>Compliance expertise</strong> and protocol understanding</p>
              </div>
              <div className="why-point" data-testid="why-specialist">
                <div className="why-icon">★</div>
                <p><strong>Investigator meeting specialists</strong> with proven success in high-stakes pharma events</p>
              </div>
            </div>

            <div className="why-quote" data-testid="why-quote">
              <p>"We don't just manage events — we speak your language and understand your stakes."</p>
            </div>
          </div>
        </div>
      </section>

      {/* People Love Us Section */}
      <section className="section-gray" data-testid="people-love-section">
        <div className="section-container">
          <h2 className="section-title" data-testid="love-title">People Love Us For</h2>
          <div className="section-divider"></div>

          <div className="love-grid">
            <div className="love-item" data-testid="love-themes"><span className="love-bullet">★</span> Our Beautiful Themes & Decors</div>
            <div className="love-item" data-testid="love-venues"><span className="love-bullet">★</span> Exclusive & Exotic Venues</div>
            <div className="love-item" data-testid="love-attitude"><span className="love-bullet">★</span> Team's 'Will Do' Attitude and Frankness</div>
            <div className="love-item" data-testid="love-availability"><span className="love-bullet">★</span> 24×7 availability</div>
            <div className="love-item" data-testid="love-customized"><span className="love-bullet">★</span> Customized Meetings</div>
            <div className="love-item" data-testid="love-gifts"><span className="love-bullet">★</span> Personalized and Unique Gifts</div>
            <div className="love-item" data-testid="love-managers"><span className="love-bullet">★</span> Dedicated Project Managers</div>
            <div className="love-item" data-testid="love-logistics"><span className="love-bullet">★</span> Dedicated team for Logistical Support</div>
            <div className="love-item" data-testid="love-rates"><span className="love-bullet">★</span> Our tie-up's with Hotel chains and artists for Best rates</div>
          </div>
        </div>
      </section>

      {/* Global Presence Section */}
      <section id="global" className="section-white" data-testid="global-section">
        <div className="section-container">
          <h2 className="section-title" data-testid="global-title">Global Reach · Local Expertise</h2>
          <div className="section-divider"></div>

          <p className="global-intro" data-testid="global-intro">
            Successfully executed meetings in Malaysia, UK, Spain, Australia, Thailand, USA, China, Dubai, Singapore, and across major Indian cities — the list continues.
          </p>

          <div className="global-marquee" data-testid="global-marquee">
            <div className="marquee-content">
              <span>Malaysia</span>
              <span>•</span>
              <span>United Kingdom</span>
              <span>•</span>
              <span>Spain</span>
              <span>•</span>
              <span>Australia</span>
              <span>•</span>
              <span>Thailand</span>
              <span>•</span>
              <span>USA</span>
              <span>•</span>
              <span>China</span>
              <span>•</span>
              <span>Dubai</span>
              <span>•</span>
              <span>Singapore</span>
              <span>•</span>
              <span>India</span>
              <span>•</span>
              <span>Malaysia</span>
              <span>•</span>
              <span>United Kingdom</span>
              <span>•</span>
              <span>Spain</span>
              <span>•</span>
              <span>Australia</span>
            </div>
          </div>

          <div className="global-cities" data-testid="global-cities">
            <h3>Major Cities Served</h3>
            <div className="cities-list">
              <span>Kuala Lumpur</span>
              <span>London</span>
              <span>Madrid</span>
              <span>Sydney</span>
              <span>Bangkok</span>
              <span>New York</span>
              <span>Beijing</span>
              <span>Singapore</span>
              <span>Bangalore</span>
              <span>Mumbai</span>
              <span>Goa</span>
              <span>Chennai</span>
              <span>New Delhi</span>
              <span>Melbourne</span>
              <span>Dubai</span>
            </div>
          </div>
        </div>
      </section>

      {/* Expertise Section */}
      <section id="expertise" className="section-gray" data-testid="expertise-section">
        <div className="section-container">
          <h2 className="section-title" data-testid="expertise-title">Our Expertise</h2>
          <div className="section-divider"></div>

          <Accordion type="single" collapsible className="expertise-accordion">
            <AccordionItem value="pharma-consulting" data-testid="expertise-pharma-consulting">
              <AccordionTrigger className="accordion-trigger">
                <div className="accordion-title">Pharma & Clinical Trial Consulting</div>
              </AccordionTrigger>
              <AccordionContent className="accordion-content">
                <div className="expertise-category">
                  <h4>Expert Solutions Across APAC & MEA</h4>
                  <ul>
                    <li><strong>Regional Expertise Network</strong> – Niche consultants with deep experience across APAC, Middle East, and Africa</li>
                    <li><strong>Core Competencies</strong> – Regulatory, Clinical Operations, Medical Writing, Safety, Biostatistics, and Quality Assurance</li>
                    <li><strong>CRO & Sponsor Solutions</strong> – Specialized consultants for CRO oversight, engagement strategy, and performance optimization</li>
                    <li><strong>Site Excellence</strong> – Site feasibility assessments, qualifications, activation, and strategic engagement</li>
                    <li><strong>Operational Excellence</strong> – Therapeutic training, SOP development and updates, process mapping, and workflow optimization</li>
                    <li><strong>Translation Services</strong> – Certified translation of regulatory, clinical, and medical documentation across major international languages</li>
                  </ul>
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="corporate" data-testid="expertise-corporate">
              <AccordionTrigger className="accordion-trigger">
                <div className="accordion-title">Corporate & Leadership Events</div>
              </AccordionTrigger>
              <AccordionContent className="accordion-content">
                <div className="expertise-category">
                  <h4>Corporate Launches</h4>
                  <p>We mark each launch with distinction. All of our product launches are designed with dedication, skill and creativity. Special attention is given to even the smallest detail of theme. We create unique, extraordinary and unforgettable launches that ensure a memorable experience from beginning to end. We have spanned the globe, exceeding our client's expectations from New York to New Delhi.</p>
                </div>
                <div className="expertise-category">
                  <h4>Leadership Meetings/Off-Site Meetings</h4>
                  <p>Unique and exclusive destinations and venues. Special attention is given to even the smallest detail of leadership requirements. Global Leaders on Panel for Leadership Workshops. We manage end to end requirements including travel & Visa support. Meetings as exclusive as 10 leaders to offsite meetings of 100+ executives, all of our meetings are designed with dedication, skill and creativity. We have spanned the globe, exceeding our client's expectations from Australia to Masai Mara.</p>
                </div>
                <div className="expertise-category">
                  <h4>Corporate Team Building Annual/Family Events</h4>
                  <p>Exclusive venues. Experienced team for team building activities. Unique themes and activities. Global leaders on panel for talks & interactions. Tie-up with global and local artists. Tons of ideas to engage the families and employees creating lifetime memories to take home. Turn-Key solutions including the travel, to & from venue for all. Exceeding our client's expectations from Bangalore to NCR area.</p>
                </div>
                <div className="expertise-category">
                  <h4>Conferences / Hybrid Meetings</h4>
                  <p>Experience in Medical & IT Conferences. Parallel conference management. A platform for Hybrid meetings, if required for those who can't attend. Flawless connection experiences and ease to use online platform including payment portals. Conference specific interactive App, Online/Digital Voting, etc. Every attendee is treated as a VIP beginning with the registration process and continuing through the conference end. Speakers Engagement & Expectation management. Sponsor Displays & Stall Managements. Experienced team for national & international conferences across globe.</p>
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="pharma" data-testid="expertise-pharma">
              <AccordionTrigger className="accordion-trigger">
                <div className="accordion-title">Pharma & Clinical Events</div>
              </AccordionTrigger>
              <AccordionContent className="accordion-content">
                <div className="expertise-category">
                  <h4>Investigator Meetings</h4>
                  <p>We recognize the importance of the relationship between the physicians and the pharmaceutical company. Every attendee is treated as a VIP beginning with the invitation process and continuing through the program's end. A platform for Hybrid meetings, if required for those who can't attend with Flawless connection experiences and ease to use online platform. High Quality Recordings for use at later stages. Online/Digital Voting systems. Tie up with all major chains of Hotels and Airlines for Best Rates and services. Multiple vendors for Ground Transport in Countries & Specialized team for the Logistic support including the Document designing and printing.</p>
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="social" data-testid="expertise-social">
              <AccordionTrigger className="accordion-trigger">
                <div className="accordion-title">Social & Experiential Events</div>
              </AccordionTrigger>
              <AccordionContent className="accordion-content">
                <div className="expertise-category">
                  <h4>Weddings / Social Events</h4>
                  <p>Exclusive, Exotic & Beautiful venues & destinations. Experienced Teams that take care for venue, card designing, apparel selection, photoshoots and logistics, etc. Every attendee is treated as a VIP beginning with the invitation process and continuing through the event end. Unique theme ideas and exclusive Decor themes curated based on your imaginations. Turn-key solutions from guest list creation to return gift and travel arrangements. Tie up with local & global artists to create moments to cherish for lifetime. Events hosted by our team for even you to attend as Guests. We understand budgets vary and our teams provides for you the best possible experience at your ease.</p>
                </div>
                <div className="expertise-category">
                  <h4>School Events</h4>
                  <p>We undertake responsibility to create school events engaging children, parents and community around the year. We curate events from Science Fairs, athletic meets, medical camps, annual days, literature fests to fun fairs. We understand the importance of the relationship between the school, children and community. Every attendee is treated with love and care. A special importance is given to showcase children's creativity and school's role in life of community. Tie-up with leading schools for teacher's workshops too. Children create memories for lifetime and go home proud and motivated.</p>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="section-white" data-testid="testimonials-section">
        <div className="section-container">
          <h2 className="section-title" data-testid="testimonials-title">Our Motivation's</h2>
          <div className="section-divider"></div>

          <div className="testimonials-grid">
            <div className="testimonial-card" data-testid="testimonial-1">
              <div className="testimonial-quote-mark">"</div>
              <p className="testimonial-text">We had a great experience at the Executives Off-Site meeting organized superbly by <span style={{ color: '#AD7900' }}>PE360</span>, where the team took care of even minute details leading us to relax and concentrate on our main objectives.</p>
              <div className="testimonial-stars">★★★★★</div>
              <p className="testimonial-author">Sujoy Choudhary</p>
              <p className="testimonial-location">Switzerland</p>
            </div>

            <div className="testimonial-card" data-testid="testimonial-2">
              <div className="testimonial-quote-mark">"</div>
              <p className="testimonial-text">It was great to see the theme of our imagination become a reality. Thanks to <span style={{ color: '#AD7900' }}>PE360</span> team for creating the Event beyond what we could have imagined and taking care of all guests beyond our expectations.</p>
              <div className="testimonial-stars">★★★★★</div>
              <p className="testimonial-author">Ira Arora</p>
              <p className="testimonial-location">Australia</p>
            </div>

            <div className="testimonial-card" data-testid="testimonial-3">
              <div className="testimonial-quote-mark">"</div>
              <p className="testimonial-text"><span style={{ color: '#AD7900' }}>PE360</span> team handled the conference of international delegates for us, and it was great to see how the team coordinated with speakers and delegates and making sure the conference went smoothly and with perfect timing.</p>
              <div className="testimonial-stars">★★★★★</div>
              <p className="testimonial-author">Dr. D.V. Chalapathy</p>
              <p className="testimonial-location">India</p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="section-contact" data-testid="contact-section">
        <div className="section-container">
          <h2 className="section-title-light" data-testid="contact-title">Let's Plan Your Next Event</h2>
          <div className="section-divider-light"></div>

          <div className="contact-content">
            <div className="contact-info">
              <div className="contact-item" data-testid="contact-email">
                <Mail size={24} />
                <div>
                  <div className="contact-label">Email</div>
                  <a href="mailto:rupreet.kaur@pharmaevents360.com" className="contact-link">rupreet.kaur@pharmaevents360.com</a>
                </div>
              </div>

              <div className="contact-item" data-testid="contact-phone">
                <Phone size={24} />
                <div>
                  <div className="contact-label">Mobile</div>
                  <a href="tel:+919900536000" className="contact-link">(+91) 9900536000</a>
                </div>
              </div>

              <div className="contact-item" data-testid="contact-hours">
                <MessageSquare size={24} />
                <div>
                  <div className="contact-label">Office Hours</div>
                  <p className="contact-text">8 AM – 8 PM (IST)</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer" data-testid="footer">
        <p>© 2025 <span style={{ color: '#AD7900' }}>PE360</span> | Global Event Solutions Partner</p>
      </footer>
    </div>
  );
}

export default Home;
